import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import 'moment/locale/fr';
import { AuthService } from '../../auth/auth.service';
import { UserDto } from '../../auth/user.dto';
import { AlertComponent } from '../../shared/alert/alert.component';
import { ConfirmationComponent } from '../../shared/confirmation/confirmation.component';
import { BeneficiariesService } from '../../shared/services/beneficiaries.service';
import { Utils } from '../../shared/utils';
import { BeneficiaryDuplicateCheckDto } from './beneficiary-duplicate-check.dto';

type ErrorPayload = {
  beneficiaryId: number;
  structure: string;
  orientationEndDate: Date;
};

@Component({
  selector: 'app-beneficiary-duplicate-check',
  templateUrl: './beneficiary-duplicate-check.component.html',
  standalone: false,
})
export class BeneficiaryDuplicateCheckComponent implements OnInit {
  public form: FormGroup<{
    approval: FormControl<boolean>;
    firstName: FormControl<string | null>;
    usualName: FormControl<string | null>;
    birthDate: FormControl<Date | null>;
  }>;
  public submitted: boolean = false;
  public currentUser: UserDto;

  private confirmCallback: (data?: BeneficiaryDuplicateCheckDto) => void;

  constructor(
    private readonly modal: NgbActiveModal,
    private readonly beneficiariesService: BeneficiariesService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly modalService: NgbModal,
  ) {}

  public async ngOnInit(): Promise<void> {
    this.currentUser = await this.authService.getUser();

    this.form = new FormGroup({
      approval: new FormControl(false, [Validators.requiredTrue]),
      firstName: new FormControl(null, [Validators.required, Validators.maxLength(32), Validators.pattern(Utils.alphabeticalPattern)]),
      usualName: new FormControl(null, [Validators.required, Validators.maxLength(32), Validators.pattern(Utils.alphabeticalPattern)]),
      birthDate: new FormControl(null, [Validators.required]),
    });
  }

  public async confirm(): Promise<BeneficiaryDuplicateCheckDto> {
    return new Promise<BeneficiaryDuplicateCheckDto>((resolve: (value: BeneficiaryDuplicateCheckDto) => void) => {
      this.confirmCallback = (data?: BeneficiaryDuplicateCheckDto) => {
        this.modal.close();
        resolve(data);
      };
    });
  }

  public async accept(): Promise<void> {
    this.submitted = true;
    if (this.form.valid) {
      const dto: BeneficiaryDuplicateCheckDto = {
        firstName: this.form.value.firstName,
        usualName: this.form.value.usualName,
        birthDate: this.form.value.birthDate,
      } as BeneficiaryDuplicateCheckDto;
      try {
        await this.beneficiariesService.checkForDuplicate(dto);
        this.confirmCallback(dto);
      } catch (error) {
        if (error.status !== 460) {
          return;
        }
        if ((this.currentUser?.groups ?? []).includes('ADMIN')) {
          this.handleDuplicateForAdmin(dto);
        } else if ((this.currentUser?.groups ?? []).includes('RECEPTION')) {
          this.handleDuplicateForReception(error.error as ErrorPayload);
        } else {
          this.handleDuplicateForOther(error.error as ErrorPayload);
        }
      }
    }
  }

  public cancel(): void {
    this.router.navigate(['beneficiaries']);
    this.modal.close();
  }

  private async handleDuplicateForAdmin(dto: BeneficiaryDuplicateCheckDto): Promise<void> {
    const modal: NgbModalRef = this.modalService.open(ConfirmationComponent, {
      backdrop: 'static',
      centered: true,
    });
    const modalComponent: ConfirmationComponent = modal.componentInstance as ConfirmationComponent;
    const confirm: boolean = await modalComponent.confirm('L’individu que vous voulez créer existe déjà. Êtes-vous sûr de vouloir le créer ?');
    if (confirm) {
      this.confirmCallback(dto);
    } else {
      this.cancel();
    }
  }

  private handleDuplicateForReception(error: ErrorPayload) {
    if (error.beneficiaryId) {
      this.router.navigate(['beneficiaries', error.beneficiaryId, 'details']);
      this.modal.close();
    }
  }

  private async handleDuplicateForOther(error: ErrorPayload): Promise<void> {
    let message: string;
    if (error.orientationEndDate !== undefined) {
      message =
        "L'individu que vous voulez créer existe déjà et était suivi par la structure " +
        error.structure +
        " jusqu'au " +
        moment(error.orientationEndDate).locale('fr').format('DD/MM/YYYY') +
        ". Vous pouvez contacter cette structure pour plus de renseignements ou l'administrateur Insertis pour réouvrir le dossier.";
    } else {
      message =
        "L'individu que vous voulez créer existe déjà et est suivi dans la structure " +
        error.structure +
        ". Vous pouvez contacter cette structure ou l'administrateur Insertis pour plus de renseignements.";
    }
    const modal: NgbModalRef = this.modalService.open(AlertComponent, {
      backdrop: 'static',
      centered: true,
    });
    const modalComponent: AlertComponent = modal.componentInstance as AlertComponent;
    await modalComponent.alert(message);
    this.cancel();
  }
}
